import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import _ from 'lodash'

import TabPanel from '@/components/ui/tab/TabPanel'
import Tabs from '@/components/ui/tabs/Tabs'
import { Container } from '@mui/material'

import { OfflineConsultation } from '@/core/offline'
import OfflinePC300Mesures from './OfflinePC300Mesures'
import OfflineBiologicals from './OfflineBiologicals'
import OfflineDiagnosticMedicalVideo from './OfflineDiagnosticMedicalVideo'
import OfflineDiagnosticStethoscope from './OfflineDiagnosticStethoscope'
import useOfflineMedconnectDevices from '../model/useOfflineMedconnectDevices'

type OfflineAllDiagnosticsProps = {
  consultation: OfflineConsultation
  onConsultationUpdate?: (consultation: OfflineConsultation) => void
}

export const OfflineAllDiagnostics = React.memo(function OfflineAllDiagnostics({ consultation, onConsultationUpdate }: OfflineAllDiagnosticsProps) {
  const { t } = useTranslation()

  // Initialize Offline Medconnect devices hook for offline medical device communication
  const { setDeviceTracker, deviceTracker, status } = useOfflineMedconnectDevices(true, consultation, onConsultationUpdate || (() => {}))

  const handleConsultationUpdate = useCallback(
    (updatedConsultation: OfflineConsultation) => {
      if (onConsultationUpdate) {
        onConsultationUpdate(updatedConsultation)
      }
    },
    [onConsultationUpdate]
  )

  return (
    <Container maxWidth="md" sx={{ pt: '20px', pb: '20px' }}>
      <Tabs
        renderTabPanel={({ value }) => (
          <>
            <TabPanel value={value} index={0}>
              <OfflinePC300Mesures
                consultation={consultation}
                onConsultationUpdate={handleConsultationUpdate}
                deviceTracker={deviceTracker}
                setDeviceTracker={setDeviceTracker}
                pcStatus={status}
              />
            </TabPanel>
            <TabPanel value={value} index={1}>
              <OfflineDiagnosticMedicalVideo consultation={consultation} onConsultationUpdate={handleConsultationUpdate} videoHeight="75vh" videoWidth="100%" />
            </TabPanel>
            <TabPanel value={value} index={2}>
              <OfflineDiagnosticStethoscope consultation={consultation} onConsultationUpdate={handleConsultationUpdate} />
            </TabPanel>
            <TabPanel value={value} index={3}>
              <OfflineBiologicals consultation={consultation} onConsultationUpdate={handleConsultationUpdate} />
            </TabPanel>
          </>
        )}
        tabs={_.compact([
          { label: t('page.localConsultation.sidebar.mesures.label'), sx: { fontWeight: 700 }, id: 'mesures' },
          { label: t('page.liveConsultation.sidebar.medicalVideo.label'), sx: { fontWeight: 700 }, id: 'medicalVideo' },
          { label: t('page.localConsultation.sidebar.stethoscope.label'), sx: { fontWeight: 700 }, id: 'stethoscope' },
          { label: t('page.localConsultation.sidebar.biologicals.label'), sx: { fontWeight: 700 }, id: 'biologicals' },
        ])}
        variant="fullWidth"
      />
    </Container>
  )
})

export default OfflineAllDiagnostics
